import { usePiano } from "@sciam/piano/react";
import { useEmailCollectorCheck } from "@sciam/shared";
import { useAuth } from "~features/auth";
import { useUser } from "~/features/user";
import { useSciAmJwtSync } from "~features/piano/hooks/use-piano-auth-sync";
import { useEntitlements } from "~features/access";

/** @typedef {{ id: string, text: string, href?: string, button?: string, onClick?: (e: MouseEvent)=>void }} CallToAction */

// Sell this product before showing any other promos
const ACTIVE_SUB_PROMO = "games";
const NONACTIVE_SUB_PROMO = "digital90Days1Dollar_nonsubscribers";

/** @type {Record<string, Record<string,CallToAction>>} */
export const CTA = {
  sciamEvent: {
    darkEnergy: {
      id: "sciamevent__signup-darkenergy",
      href: "https://us06web.zoom.us/webinar/register/WN_OTrkJtehS6qW4uzgPSgXXA#/registration?utm_source=site&utm_medium=website&utm_campaign=active_webinar_120424&utm_content=Text&utm_term=v1_s1",
      text: "Sign up now for our subscriber event on dark energy",
    },
  },
  newsletter: {
    default: {
      id: "newsletter__signup-1",
      href: "/newsletters/",
      text: "Sign Up for Our Daily Newsletter",
    },
  },
  subscription: {
    default: {
      id: "getsciam-2",
      href: "/getsciam/?utm_source=site&utm_medium=display",
      text: "Subscribe to Scientific American",
    },
    trial: {
      id: "sub-promo-banner",
      href: "/getsciam/60-day-offer/?utm_source=site&utm_medium=display&utm_campaign=june24-marketing&utm_term=homepage_banner",
      text: "Digital Access for $1",
    },
    // Evergreen promo for digital
    digitalEvergreen: {
      id: "sub-promo-banner",
      href: "/getsciam/digital-sale/?utm_source=site&utm_medium=display&utm_campaign=may24-marketing20240506_digital20banner&utm_term=LP_v1_s1_display_homepage",
      text: "Save 20% on a Digital Subscription",
    },
    games: {
      id: "games-banner",
      href: "/games/?utm_source=site&utm_medium=display&utm_campaign=mar25-marketing&utm_term=LP_games_v1_s1",
      text: "Play SciAm Games",
    },
    // 60 days of digital access for $1
    sixtyDayTrial: {
      id: "60-day-offer",
      href: "/getsciam/60-day-offer/?utm_source=site&utm_medium=display&utm_campaign=nov24-marketing&utm_term=LP_$1for60days_v1_s1",
      text: "60 days of Digital Access for $1",
    },
    sixtyPercentOffUnlimited: {
      id: "60-percent-off-unlimited",
      href: "/getsciam/?utm_source=site&utm_medium=display&utm_campaign=nov24-marketing&utm_term=LP_blackfriday_v1_s1",
      text: "Get 60% Off Unlimited Access!",
    },
    // Buy 1 year digital, get 2 years instead
    buy1yrGet2yrDigital: {
      id: "1-year-get-2-year-digital",
      href: "/getsciam/?utm_source=site&utm_medium=display&utm_campaign=dec24-marketing&utm_term=LP_cybermonday_v1_s1",
      text: "Buy 1 Year, Get 2 Years of Digital Access!",
    },
    // Give a gift sub for 20% off all subs
    gift20percentOffAllSubs: {
      id: "gift-20-percent-off-all-subs",
      href: "/getsciam/gift/?utm_source=site&utm_medium=display&utm_campaign=dec24-marketing&utm_term=LP_giftgiving_v1_s1",
      text: "Give a Gift: Save 20% on all Subscriptions",
    },
    // Give a gift sub for 20% off all subs for non-subscribers
    gift20percentOffAllSubs_nonsubscribers: {
      id: "gift-20-percent-off-all-subs-for-non-subscribers",
      href: "/getsciam/gift/?utm_source=site&utm_medium=display&utm_campaign=dec24-marketing&utm_term=LP_giftgiving_v2_s1",
      text: "Give a Gift: Save 20% on all Subscriptions",
    },
    // 50% off Unlimited
    unlimited50PercentOff_nonsubscribers: {
      id: "unlimited-50-percent-off-for-non-subscribers",
      href: "/getsciam/?utm_source=site&utm_medium=display&utm_campaign=dec24-marketing&utm_term=LP_unlimited50_v1_s1",
      text: "Save 50% on Unlimited",
    },
    // Print Digital promo
    printDigital: {
      id: "sub-print-digital-20-percent",
      href: "/getsciam/?utm_source=site&utm_medium=display&utm_campaign=dec24-marketing&utm_term=LP_print20_v1_s1",
      text: "Save 20% on Print & Digital",
    },
    digital90Days1Dollar_nonsubscribers: {
      id: "digital-90-days-1-dollar-for-non-subscribers",
      href: "/getsciam/90-day-offer/?utm_source=site&utm_medium=display&utm_campaign=dec24-marketing&utm_term=LP_90daytrial_v1_s1",
      text: "Special Offer: $1 for 90 days",
    },
    // Give a gift Unlimited sub for 50% off for subscribers
    giftUnlimited50PercentOff_subscribers: {
      id: "gift-unlimited-50-percent-off-for-subscribers",
      href: "/getsciam/gift/?utm_source=site&utm_medium=display&utm_campaign=dec24-marketing&utm_term=LP_giftgiving_v1_s1",
      text: "Give a Gift: Save 50% on Unlimited",
    },
    // Dark Matter Event 2025
    darkMatterEvent2025_subscribers: {
      id: "dark-matter-event-2025-banner",
      href: "https://us06web.zoom.us/webinar/register/WN_cPOyvurLS3Woiia7seqdLw#/registration",
      text: "Sign up for a subscriber-only event on dark matter!",
    },
    // Shown as an active promo to non-subscribers on the homepage
    // digital: {}, printdigital: {}, unlimited: {}
  },
  gift: {},
};

/**
 * @function useCta - Returns the appropriate CTA for the current user
 * @param {Object} config
 * @param {boolean} [config.allowUpsell=false] Upsell to existing customers
 * @param {boolean} [config.showActivePromo=false] Show the active promo to non-subscribers
 * @param {boolean} [config.preferActivePromo=false] Prioritize the active promo over any other CTA
 * @param {boolean} [config.showInSsr=false] Render the CTA in SSR (or know which CTA would render ahead of time)
 * @returns {CallToAction | null} The params for the CTA, or null when no CTA should be shown
 */
export function useCta({
  allowUpsell = false,
  showActivePromo = false,
  preferActivePromo = false,
  showInSsr = false,
} = {}) {
  const { hasAccess, hasSub, hasUnlimitedAccess, ready } = useEntitlements();
  const hasEmailSub = useEmailCollectorCheck();
  const { userId } = useUser();
  const { ready: isSciAmJwtReady } = useSciAmJwtSync();
  const isLoggedIn = !!userId; //  Indicates user has either an Auth0 JWT, a SciAm JWT, or both
  const isLoggedOut = !isLoggedIn && isSciAmJwtReady; // user is _definitely_ logged out

  // ready is a property that lets us know when the user's entitlements are loaded and can be accessed safely.
  let loadStatus = ready;
  let loggedIn = isLoggedIn;
  let notPaidSubscriber = hasSub === false;
  let notUnlimited = hasUnlimitedAccess === false;

  // If we're showing the CTA in SSR, we can allow for some uncertainty in the user's access status
  if (showInSsr) {
    loggedIn = !isLoggedOut; // loggedOut === false until auth init
    notPaidSubscriber = !hasSub; // hasSub === undefined until auth init
    notUnlimited = !hasUnlimitedAccess; // hasUnlimitedAccess === undefined until Piano or IDP check
  }

  // Don't show any CTA until we know user's access status
  if (!showInSsr && (loadStatus === "loading" || hasAccess === undefined)) {
    return null;
  }

  // Active promo gets priority
  if (preferActivePromo) {
    // @TODO Uncomment lines 102-105 to promote upsell logic
    // if (
    //   notPaidSubscriber ||
    //   // If we're upselling, don't upsell to existing unlimited users
    //   (allowUpsell && ACTIVE_SUB_PROMO === "sitewide" && notUnlimited)
    // ) {
    //   return CTA.subscription[ACTIVE_SUB_PROMO];
    // }

    // If active promo is true, then show active promo if not a paid subscriber
    if (notPaidSubscriber) {
      return CTA.subscription[NONACTIVE_SUB_PROMO];
    }
  }

  // No email sub or logged-out
  /**
   * If user has no email sub or is logged out, show current promo
   * @TODO Uncomment line 152 and comment line 153 to promote newsletter
   */
  if (!(loggedIn || hasEmailSub)) {
    // return CTA.newsletter.default;
    return CTA.subscription[NONACTIVE_SUB_PROMO];
  }

  // Logged in but not subscribed
  if (notPaidSubscriber) {
    return CTA.subscription[NONACTIVE_SUB_PROMO];
  }

  // Upsell existing users
  if (allowUpsell && loggedIn) {
    // Subscribed w/ digital subscription => upsell to print+digital
    // if (notUnlimited && noPrintDigital) return CTA.subscription.printdigital;
    // Doesn't have unlimited access => upsell to unlimited
    // if (notUnlimited) {
    //   return CTA.subscription.unlimited;
    // }
  }

  /**
   * If user is logged in and a paid subscriber, show sales banner with offer.
   */
  if (loggedIn && !notPaidSubscriber) {
    return CTA.subscription[ACTIVE_SUB_PROMO];
  }

  return null;
}

export default useCta;
